<!--
  <Calendar :standard-id="21" @on-date-change="getDate" :widget-id="928"/>
-->
<script setup lang="ts">
import {
  ref,
  onMounted,
  onBeforeUnmount,
  defineProps,
  defineEmits,
  computed,
  watch,
  inject,
  type ComputedRef
} from 'vue';
import {loadBookingCalendar} from "@/services/NfBookingCalendar";
import {asyncDelay} from "@/utilities/nonDomUtils";
import type {Moment} from "moment/moment";
import {getEnv} from "@/utilities/env";
import type {BookingEngineConfigModel} from "@/serviceWorker/workerComponents/Models/BookingEngineConfigModel";

interface CalendarInstance {
    template?: HTMLDivElement;
    destroy: () => void;
    setDates: (start: string, end?: string) => Date[];
    openCalendar: () => boolean;
    closeCalendar: () => boolean;
    setStateProp: (key: string, value: unknown) => void;
    refreshView: () => void;
  }

  const props = defineProps<{
    standardId: number;
    widgetId: number;
    maxDate?: Date;
    minDate?: Date;
  }>();

  const emit = defineEmits<{
    (e: 'onDateChange', dates: Date[]): void
  }>();
  let dateFromOnTopCalendar = window.sessionStorage.getItem('nf-date-from') as string | undefined | Date;
  let languageCode = computed(() => window.localStorage.getItem('NfBookingEngine.languageCode') as string || 'pl');
  const calendarContainer = ref<HTMLElement | null>(null);
  let calendarInstance: CalendarInstance;


  const isMobile = ref<boolean>(false);
  const checkIsMobile = () => {
    isMobile.value = window.innerWidth <= 1255;
  }

  const initializeCalendar = () => {
    if (calendarContainer.value && typeof window.nfHotelBooking !== 'undefined') {
      const setupCalendar = window.nfHotelBooking.setupCalendar;
      const uid = props.widgetId + '-standard-availability-' + props.standardId;
      const from: Moment = window.moment(dateFromOnTopCalendar);
      const to: Moment = window.moment(dateFromOnTopCalendar).add(4, 'years');
      const config = inject<ComputedRef<BookingEngineConfigModel> | null>('config', null);
      const maxRange = config?.value?.max_stay || 20;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      calendarInstance = setupCalendar({
        container: calendarContainer.value,
        rangeSelection: true,
        minDate: from.toDate(),
        maxDate: to.toDate(),
        clearMode: true,
        maxRange,
        single: isMobile.value,
        currentLanguage: languageCode.value,
        calendarId: uid,
        key: uid,
        onDateSelect: (dates: Date[]) => {
          emit('onDateChange', dates);
        },
        headless: true,
        nfhotel: {
          standardId: props.standardId,
          instanceId: props.widgetId,
        },
        apiHost: getEnv('VITE_NFHOTEL_HOST'),
        apiProtocol: getEnv('VITE_NFHOTEL_PROTOCOL'),
        style: {
          zIndex: '9999999'
        }
      });

      // Osadzenie kalendarza w kontenerze
      if (calendarInstance && calendarInstance.template) {
        setTimeout(() => {
          calendarInstance.refreshView();
        });
        calendarContainer.value?.appendChild(calendarInstance.template);
      } else {
        console.error('Nie udało się zainicjować kalendarza');
      }
    } else {
      console.error('nfHotelBooking lub setupCalendar nie jest dostępny');
    }
  }

const onLoadFromTopMenu = () => {
  const topMenuDates = sessionStorage.getItem('top_menu');
  if (!topMenuDates) return;
  const dates = JSON.parse(topMenuDates) as { from: string, to: string };
  if (dates && dates.from && dates.to) {
    calendarInstance.setDates(dates.from,dates.to);
  }
}

  const onLoadDate = () => {
    if (props.minDate) {
      dateFromOnTopCalendar = props.minDate;
    } else if (dateFromOnTopCalendar) {
      dateFromOnTopCalendar = window.moment(dateFromOnTopCalendar).toDate();
    } else {
      dateFromOnTopCalendar = window.moment().startOf('day').toDate();
    }
  }


  const destroyCalendar = () => {
    if (calendarInstance && typeof calendarInstance.destroy === 'function') {
      calendarInstance.destroy();
    }
  }

  const onInit = async() => {
    if (!props.widgetId) {
      console.error('undefined widget id!');
      return;
    }
    if(!self.nfHotelBooking) {
      loadBookingCalendar();
      while (!self.nfHotelBooking) {
        await asyncDelay(100);
      }
    }
    onLoadDate();
    initializeCalendar();
    onLoadFromTopMenu();
  }

  watch(isMobile,(newVal, oldVal) => {
    if(newVal !== oldVal) {
      destroyCalendar();
      calendarContainer.value && (calendarContainer.value.innerHTML = '');
      onInit();
    }
  })




  onMounted(async () => {
    checkIsMobile();
    try {
      onInit();
      window.addEventListener('resize', checkIsMobile);
    } catch (error) {
      console.error('Błąd ładowania kalendarza:', error);
    }
  });

  onBeforeUnmount(() => {
    destroyCalendar();
    window.removeEventListener('resize', checkIsMobile);
  });
</script>

<template>
    <div ref="calendarContainer" class="calendar-wrapper"></div>
</template>

<!--jeśli nie złapie styli-a zapewne nie złapie to jest to dokumentacja jak stylować wrapper.-->
<style scoped>
  .calendar-wrapper {
    width: 100%;
    height: auto;
    padding: 20px 0;
    border: 1px solid #ccc;
    border-radius: 8px;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ::v-deep .nf-caldr-top {
    display: flex !important;
  }
</style>
